// EnvNoticeCard — aviso ACIONÁVEL quando um run pausou por AMBIENTE
// (RunOutcome.state === 'environment_unavailable'): sem node_modules não deu pra
// VERIFICAR, então o plano PAUSA em vez de fabricar verde (P1/P1b). Este card (P2)
// renderiza as 3 ações que o próprio outcome carrega:
//   • Instalar dependências → roda o install (run_command) no cwd da sessão
//   • Ver comandos          → mostra o comando exato pra rodar manual
//   • Marcar como não-verificável → conclui o plano MARCADO (implemented_unverified)
//
// Puro-props + dispatch direto no store (planState.planId === sessionId), então
// renderiza DENTRO do TodoDropdown sem crescer a SessionWorkspace (dp-loc zero-headroom).

import { useState } from 'react';
import { AlertTriangle, Loader2, Check, Terminal } from 'lucide-react';
import { runTool } from '../../lib/agentTools.js';
import { dispatchPlan } from '../../lib/planStateStore.js';
import { makeOutcome, RUN } from '../../lib/outcome/runOutcome.js';

const isOk = (out) => /^exitCode:\s*0\b/m.test(String(out || ''));
// window.apolo (IPC do Electron) não é declarado no tscheck — cast p/ any evita
// TS2339 sem somar erro ao ratchet codetab. Ausente na web/Pages → install desabilita.
const hasShell = () => {
  const w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});
  return !!w.apolo?.shell?.exec;
};

// Rótulo do botão de install por estado (extraído p/ manter a função de render magra).
function InstallLabel({ status, fallback }) {
  if (status === 'running') return (<><Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden="true" /> Instalando…</>);
  if (status === 'done') return (<><Check className="h-3.5 w-3.5" aria-hidden="true" /> Instalado</>);
  return fallback || 'Instalar dependências';
}

/** @param {{ outcome:object, sessionId:string }} props */
export default function EnvNoticeCard({ outcome, sessionId }) {
  const [showCmds, setShowCmds] = useState(false);
  const [install, setInstall] = useState({ status: 'idle', output: '' }); // idle|running|done|error
  if (!outcome || outcome.state !== RUN.ENVIRONMENT_UNAVAILABLE) return null;

  const actions = outcome.actions || [];
  const installAction = actions.find((a) => a.id === 'install');
  const cmd = installAction?.command || outcome.evidence?.installCmd || 'npm install';
  const root = outcome.evidence?.root || null;
  const canInstall = hasShell() && !!root && install.status !== 'running' && install.status !== 'done';

  const onInstall = async () => {
    setInstall({ status: 'running', output: '' });
    const out = await runTool('run_command', { command: cmd }, { root, sessionId });
    setInstall({ status: isOk(out) ? 'done' : 'error', output: out });
  };

  const onSkip = () => dispatchPlan(sessionId, {
    type: 'run_result',
    at: Date.now(),
    outcome: makeOutcome({
      state: RUN.IMPLEMENTED_UNVERIFIED,
      verifierRan: false,
      reason: 'user_skipped_verification',
      detail: 'Marcado como não-verificável pelo usuário — segue sem verde.',
    }),
  });

  return (
    <section aria-label="Ambiente indisponível para verificação" className="mt-1 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-apolo-warning" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-apolo-textSecondary">{outcome.detail}</p>

          {install.status === 'done' && (
            <p className="mt-1 flex items-center gap-1 text-xs text-apolo-success">
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Dependências instaladas — rode a verificação de novo.
            </p>
          )}
          {install.status === 'error' && (
            <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap rounded border border-apolo-borderSubtle bg-apolo-surface2 p-1.5 font-mono text-[11px] text-apolo-danger" data-testid="env-install-error">{String(install.output).slice(-600)}</pre>
          )}
          {showCmds && (
            <pre className="mt-1 overflow-x-auto rounded border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 font-mono text-[11px] text-apolo-textPrimary" data-testid="env-cmds">{root ? `cd ${root}\n` : ''}{cmd}</pre>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" onClick={onInstall} disabled={!canInstall} className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing disabled:cursor-not-allowed disabled:opacity-50">
              <InstallLabel status={install.status} fallback={installAction?.label} />
            </button>
            <button type="button" onClick={() => setShowCmds((v) => !v)} aria-expanded={showCmds} className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle px-2.5 py-1 text-xs font-medium text-apolo-textSecondary transition hover:bg-apolo-surface2 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing">
              <Terminal className="h-3.5 w-3.5" aria-hidden="true" /> Ver comandos
            </button>
            <button type="button" onClick={onSkip} className="rounded-md border border-apolo-borderSubtle px-2.5 py-1 text-xs font-medium text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textSecondary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing">
              Marcar como não-verificável
            </button>
          </div>

          {!root && (
            <p className="mt-1 text-[11px] text-apolo-textTertiary">Raiz da sessão indisponível — rode o comando manualmente (Ver comandos).</p>
          )}
        </div>
      </div>
    </section>
  );
}
